import { useState } from 'react';

import { Button, Sheet } from '../components/index.ts';
import { syncRepository } from '../db/repositories/sync.ts';
import { cx } from '../lib/cx.ts';
import { syncNow } from './engine.ts';
import { describeAge, useSyncStatus } from './useSyncStatus.ts';
import type { SyncState } from './useSyncStatus.ts';

/*
 * Colour, a word, and a shape — never colour alone.
 *
 * Roughly one man in twelve has a colour vision deficiency and this is a
 * specialty with a lot of men in it, so "is my work safe?" cannot be answered
 * by a hue. Each state gets a distinct silhouette as well: a filled circle when
 * everything is backed up, a diamond while entries are waiting, a hollow ring
 * when there is no network, and a square when something is actually failing.
 */
const LOOK: Record<SyncState, { dot: string; pill: string; label: string }> = {
  synced: {
    dot: 'rounded-full bg-success',
    pill: 'bg-success-quiet text-success',
    label: 'Backed up',
  },
  pending: {
    dot: 'rotate-45 rounded-xs bg-warning',
    pill: 'bg-warning-quiet text-warning',
    label: 'Waiting',
  },
  offline: {
    dot: 'rounded-full border-2 border-current bg-transparent',
    pill: 'bg-surface-raised text-muted',
    label: 'Offline',
  },
  problem: {
    dot: 'rounded-xs bg-danger',
    pill: 'bg-danger-quiet text-danger',
    label: 'Problem',
  },
};

/**
 * The sync state, quietly, in the header of every screen — and the panel behind
 * it.
 *
 * Hazard H2 is a trainee believing their work is backed up when it is not. The
 * defence is not a louder alert; it is that the true state is always on screen
 * and always says the less comforting of two readings.
 */
export function SyncIndicator({ userId }: { userId: string | null }) {
  const status = useSyncStatus(userId);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  if (userId === null) return null;

  const look = LOOK[status.state];
  const summary =
    status.state === 'synced'
      ? look.label
      : `${look.label}${status.pending > 0 ? ` · ${String(status.pending)}` : ''}`;

  async function runSync(): Promise<void> {
    if (userId === null) return;

    setBusy(true);
    try {
      await syncNow(userId);
    } finally {
      setBusy(false);
    }
  }

  async function retryFailed(): Promise<void> {
    if (userId === null) return;

    setBusy(true);
    try {
      await syncRepository.retryFailed(userId);
      await syncNow(userId);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={`Backup status: ${summary}`}
        className="flex min-h-touch items-center"
      >
        {/*
          A pill rather than a bare dot. This is the H2 control — a trainee
          believing their work is safe when it is not — so it is deliberately
          more visible than a tasteful grey dot would be, and it stays one tap
          from the panel.
        */}
        <span
          className={cx(
            'flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium',
            look.pill,
          )}
        >
          <span aria-hidden="true" className={cx('size-2 shrink-0', look.dot)} />
          <span>{summary}</span>
        </span>
      </button>

      <Sheet open={open} onClose={() => setOpen(false)} title="Backup status">
        <div className="flex flex-col gap-4">
          {status.stale ? (
            /*
             * The H2 control, and deliberately not subtle: something has been
             * waiting more than a day, which is no longer a passing blip.
             */
            <div className="rounded-md border border-danger bg-danger-quiet p-4">
              <p className="text-sm font-strong text-text">
                {status.pending === 1
                  ? 'One entry has been waiting to back up for over a day.'
                  : `${String(status.pending)} entries have been waiting to back up for over a day.`}
              </p>
              <p className="pt-1 text-sm text-muted">
                They are still on this device and nothing has been lost, but they exist here only.
                If this phone were lost today, they would go with it. Try again below, and if it
                keeps failing, export your logbook from Settings as a stopgap.
              </p>
            </div>
          ) : null}

          <dl className="flex flex-col gap-3 text-sm">
            <Row term="Now">
              <span className="inline-flex items-center gap-2">
                <span aria-hidden="true" className={cx('size-2 shrink-0', look.dot)} />
                {look.label}
              </span>
            </Row>
            <Row term="Waiting to back up">
              {status.pending === 0 ? 'Nothing' : `${String(status.pending)}`}
            </Row>
            <Row term="Last backed up">
              {status.lastSyncedAgoMs === null ? 'Not yet' : describeAge(status.lastSyncedAgoMs)}
            </Row>
            {status.failed === 0 ? null : (
              <Row term="Problems">
                <span className="text-danger">
                  {status.failed === 1
                    ? '1 entry keeps failing'
                    : `${String(status.failed)} entries keep failing`}
                </span>
              </Row>
            )}
          </dl>

          {!status.online ? (
            <p className="text-sm text-muted">
              You are offline. Logging works exactly as normal — entries are saved on this device
              and will back up on their own when you have signal again.
            </p>
          ) : null}

          <div className="flex flex-wrap gap-2">
            <Button variant="primary" loading={busy} onClick={() => void runSync()}>
              Back up now
            </Button>
            {status.failed === 0 ? null : (
              <Button variant="secondary" loading={busy} onClick={() => void retryFailed()}>
                Retry the failed ones
              </Button>
            )}
          </div>
        </div>
      </Sheet>
    </>
  );
}

function Row({ term, children }: { term: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-4 border-b border-line pb-2 last:border-b-0">
      <dt className="text-faint">{term}</dt>
      <dd className="text-text">{children}</dd>
    </div>
  );
}
